/**
 * Resolve drawing file URLs for display/fetch.
 *
 * - Legacy static file server: paths under `/files/facilities/drawings/` must hit the
 *   staticfileserver host, not the Next.js origin (which returns 404).
 * - Cloud object paths: `/files/clients/...` is served by the cloud-storage service’s
 *   `/files/` static handler, not by Next.
 * - Prefer same-origin `/api/v1/cloud-storage/files/:id/download` when that is what we
 *   stored (cookie-auth proxy).
 */

const LEGACY_FACILITIES_DRAWINGS = '/files/facilities/drawings/';

function legacyStaticFilesOrigin(): string {
  const raw = process.env.NEXT_PUBLIC_STATIC_FILE_SERVER_URL?.trim();
  if (raw) {
    const base = raw.replace(/\/?upload\/?$/i, '').replace(/\/$/, '');
    if (base) return base;
  }
  return 'https://staticfileserver.totalaccess.co.za';
}

function cloudStoragePublicOrigin(): string {
  return (process.env.NEXT_PUBLIC_CLOUD_STORAGE_URL || 'https://cloud-storage.totalaccess.co.za').replace(
    /\/$/,
    '',
  );
}

/**
 * Cloud static is mounted at /files/ with root STORAGE_DIR/clients, so URLs must be
 * /files/{clientId}/drives/... — never /files/clients/{clientId}/... (404).
 * Normalize legacy/wrong URLs for bookmarks and old API responses.
 */
function cloudBrowserPathFromFilesUrl(pathname: string): string {
  if (pathname.startsWith('/files/clients/')) {
    return `/files/${pathname.slice('/files/clients/'.length)}`;
  }
  return pathname;
}

function pathAndSearch(input: string, isAbsolute: boolean): { pathname: string; search: string } {
  try {
    const u = isAbsolute ? new URL(input) : new URL(input, 'https://placeholder.invalid');
    return { pathname: u.pathname, search: u.search };
  } catch {
    const [pathPart, ...rest] = input.split('?');
    const pathname = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
    const search = rest.length > 0 ? `?${rest.join('?')}` : '';
    return { pathname, search };
  }
}

export function resolveDrawingFileUrl(url: string | null | undefined): string {
  if (url == null) return '';
  const t = String(url).trim();
  if (!t) return '';

  const isHttp = /^https?:\/\//i.test(t);

  if (t.includes(LEGACY_FACILITIES_DRAWINGS)) {
    const { pathname, search } = pathAndSearch(t, isHttp);
    if (pathname.includes(LEGACY_FACILITIES_DRAWINGS)) {
      return `${legacyStaticFilesOrigin()}${pathname}${search}`;
    }
  }

  if (!isHttp && t.startsWith('/files/clients/')) {
    const fixed = cloudBrowserPathFromFilesUrl(t.split('?')[0]);
    const qs = t.includes('?') ? `?${t.split('?').slice(1).join('?')}` : '';
    return `${cloudStoragePublicOrigin()}${fixed}${qs}`;
  }

  if (isHttp) {
    try {
      const u = new URL(t);
      if (u.pathname.startsWith('/files/clients/')) {
        const fixedPath = cloudBrowserPathFromFilesUrl(u.pathname);
        return `${cloudStoragePublicOrigin()}${fixedPath}${u.search}`;
      }
    } catch {
      /* fall through */
    }
    return t;
  }

  if (t.startsWith('/')) return t;

  if (t.includes('staticfileserver.')) {
    return t.startsWith('http') ? t : `https://${t.replace(/^\/+/, '')}`;
  }

  return `/${t.replace(/^\/+/, '')}`;
}
